import React, { useEffect } from 'react';

import clsx from 'clsx';
import { FieldError, FormProvider, useForm } from 'react-hook-form';

import { MultipleSelectOption } from 'components/MultipleSelect/MultipleSelect';
import { LoadingType, setContractorPortalData } from 'actions';
import { setData } from 'actions/data';
import { Button } from 'components/Buttons/Button';
import FormBody from 'components/FormBody';
import FormHeader from 'components/FormHeader';
import Input from 'components/Inputs/Input';
import PhoneNumberInput from 'components/Inputs/PhoneNumberInput';
import Loader from 'components/Loader';
import { SearchLocationInput } from 'components/SearchLocationInput';
import Select from 'components/Select';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { addressRegistration } from 'utils/registrationUtils';
import {
  getErrorMessageForRequiredFields,
  getStreetNameFromStreetAddress,
  getStreetNumberFromStreetAddress,
  MONETARY_PREFIX,
  removeNonDigits,
} from 'utils/stringUtils';
import {
  getMessageForInvalidFields,
  getMessageForRequiredFields,
  isValidEmail,
  isValidYearsOfBusiness,
  validatePhoneNumber,
} from 'utils/validationUtils';
import NumberInput from 'components/Inputs/NumberInput';
import { isValidDateBeforeNow } from 'utils/dateUtils';
import states from 'enums/StatesList';
import { Checkbox, CheckboxType } from 'components/Checkbox';
import MultipleSelect from 'components/MultipleSelect';
import { saveContractorApplication } from 'thunks';
import { IntermediaryApplicationProps } from 'api/IntermediaryApplicationsApi';
import { identityFullStory } from 'utils/fullStory';
import { DateFormat } from 'enums/DateFormat';
import Tooltip from 'components/Tooltip/index';
import { InfoIcon } from 'assets/images/index';
import { FailureNotificationType } from 'notifications';
import * as notificationManager from 'notifications';

import styles from './ContractorApplication.module.scss';
import HoldAppropriateLicense from './formComponents/HoldAppropriateLicense';
import {
  ALREADY_EXISTING_EMAIL_ERROR_MESSAGE,
  ASSOCIATED_MANUFACTURE_TOOLTIP_TEXT,
  BUSINESS_ENTITY_STRUCTURE_OPTIONS,
  BUSINESS_TRADE_NAME_TOOLTIP_TEXT,
  DEFAULT_REQUIRED_MESSAGE,
  DO_YOU_INSTALL_PRODUCTS_OPTIONS,
  FEDERAL_TAX_ID_WITH_DASH_MAX_LENGTH,
  PERCENT_PLACEHOLDER,
  PRIMARY_HOME_IMPROVEMENT_CATEGORY_OPTIONS,
  YEARS_IN_BUSINESS_MAX_LENGTH,
} from './сontractorApplication.constants';
import {
  convertPercentageValue,
  convertTotalOwnersValueToNumber,
  einMask,
  getSameAddressVariable,
  prepareTotalOwnersArray,
  setDefaultValues,
  validateTaxId,
} from './сontractorApplication.utils';
import ContactPersonInformation from './formComponents/ContactPersonInformation';
import OwnerInformation from './formComponents/OwnerInformation';
import {
  BusinessEntityStructure,
  HoldLicense,
  InputLabel,
  InstallProductsYouSell,
  MainContactPosition,
  PrimaryHomeImprovementCategory,
} from './contractorApplication.enums';
import { FormDataContractorApplication } from './contractorApplications.types';
import MasterDialerAgreement from './formComponents/MasterDialerAgreement';

const ContractorApplication = () => {
  const dispatch = useAppDispatch();

  const loading = useAppSelector((state) => state.loading[LoadingType.CreateIntermediary]);
  const { isEmailError, contractorApplication } = useAppSelector((state) => state.contractorPortal);

  const mode = isEmailError ? 'onChange' : 'onSubmit';
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search.slice(1));

  const formMethods = useForm<FormDataContractorApplication>({
    mode: mode,
    defaultValues: setDefaultValues(contractorApplication || {}),
    shouldUnregister: false,
  });

  const {
    register,
    unregister,
    handleSubmit,
    clearErrors,
    formState: { errors },
    watch,
    control,
    setValue,
    setError,
    formState,
    trigger,
    getValues,
  } = formMethods;
  const watcher = watch();

  useEffect(() => {
    dispatch(
      setData({
        utmSource: params.getAll('utm_source')[0] || null,
        utmMedium: params.getAll('utm_medium')[0] || null,
        utmCampaign: params.getAll('utm_campaign')[0] || null,
        campaignId: params.getAll('campaignid')[0] || null,
        adGroupId: params.getAll('adgroupid')[0] || null,
        network: params.getAll('network')[0] || null,
        device: params.getAll('device')[0] || null,
        refer: params.getAll('refer')[0] || null,
        howDidYouHearAboutUs: params.getAll('sourcetype')[0] || null,
        howDidYouHearAboutUsComment: params.getAll('sourcename')[0] || null,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (isEmailError) {
      setError('intermediary_email', {
        type: 'manual',
        message: ALREADY_EXISTING_EMAIL_ERROR_MESSAGE,
      });
    }
  }, [isEmailError, setError]);

  useEffect(() => {
    register('intermediary_corporate_entity_name', {
      required: {
        value: !watcher.intermediary_corporate_entity_name,
        message: getMessageForRequiredFields(InputLabel.CorporateEntityName),
      },
    });
    register('intermediary_business_name', {
      required: {
        value: !watcher.intermediary_business_name,
        message: getMessageForRequiredFields(InputLabel.BusinessTradeName),
      },
    });
    register('intermediary_federal_tax_id', {
      required: {
        value: !watcher.intermediary_business_name,
        message: getMessageForRequiredFields(InputLabel.FedTaxId),
      },
      validate: (value) => {
        return validateTaxId(value);
      },
    });
    register('intermediary_business_entity_structure', {
      required: {
        value: !watcher.intermediary_business_entity_structure,
        message: getMessageForRequiredFields(InputLabel.BusinessEntity),
      },
    });
    register('intermediary_year_of_incorporation', {
      required: {
        value: !watcher.intermediary_year_of_incorporation,
        message: getErrorMessageForRequiredFields(InputLabel.YearOfBusinessIncorporation),
      },
      validate: (value) => {
        return (
          isValidDateBeforeNow(value, DateFormat.Year) ||
          getMessageForInvalidFields(InputLabel.YearOfBusinessIncorporation)
        );
      },
    });
    register('intermediary_phone', {
      required: {
        value: !watcher.intermediary_phone,
        message: getMessageForRequiredFields(InputLabel.MainBusinessPhoneNumber),
      },
      validate: (value) => {
        return validatePhoneNumber(value);
      },
    });
    register('intermediary_email', {
      required: {
        value: !watcher.intermediary_email,
        message: getErrorMessageForRequiredFields(InputLabel.BusinessEmailAddress),
      },
      validate: (value) => {
        return isValidEmail(value) || getMessageForInvalidFields(InputLabel.BusinessEmailAddress);
      },
    });
    addressRegistration(
      register,
      {
        streetName: 'intermediary_mailing_address',
        cityName: 'intermediary_city',
        stateName: 'intermediary_state',
        zipName: 'intermediary_zip_code',
      },
      {
        streetLabel: InputLabel.MailingAddress,
        cityLabel: InputLabel.MailingCity,
        stateLabel: InputLabel.MailingState,
        zipLabel: InputLabel.MailingZip,
      },
      !watcher.same_address,
    );
    addressRegistration(
      register,
      {
        streetName: 'intermediary_corporate_address',
        cityName: 'intermediary_corporate_city',
        stateName: 'intermediary_corporate_state',
        zipName: 'intermediary_corporate_zip_code',
      },
      {
        streetLabel: InputLabel.CorporateAddress,
        cityLabel: InputLabel.CorporateCity,
        stateLabel: InputLabel.CorporateState,
        zipLabel: InputLabel.CorporateZip,
      },
    );
    register('intermediary_website_or_facebook_page', {
      required: false,
    });
    register('intermediary_how_did_you_hear_about_us', {
      required: {
        value: !watcher.intermediary_how_did_you_hear_about_us,
        message: DEFAULT_REQUIRED_MESSAGE,
      },
    });
    register('intermediary_do_you_install_the_products_you_sell', {
      required: {
        value: !watcher.intermediary_do_you_install_the_products_you_sell,
        message: DEFAULT_REQUIRED_MESSAGE,
      },
    });
    register('intermediary_associated_manufacturer_1', {
      required: {
        value: !watcher.intermediary_associated_manufacturer_1,
        message: getErrorMessageForRequiredFields(InputLabel.AssociatedManufacturer1),
      },
    });
    register('intermediary_associated_manufacturer_2', {
      required: false,
    });
    register('intermediary_annual_sales', {
      required: {
        value: !watcher.intermediary_annual_sales,
        message: getErrorMessageForRequiredFields(InputLabel.AnnualRevenue),
      },
    });
    register('intermediary_average_jobs', {
      required: {
        value: !watcher.intermediary_average_jobs,
        message: getErrorMessageForRequiredFields(InputLabel.JobsSoldPerYear),
      },
    });
    register('intermediary_percent_of_sales_financed', {
      required: {
        value: !watcher.intermediary_percent_of_sales_financed,
        message: getErrorMessageForRequiredFields(InputLabel.PercentOfSalesFinanced),
      },
      validate: (value) => {
        const percentValue = parseFloat(value + '%');

        if (isNaN(percentValue)) {
          return getMessageForInvalidFields(InputLabel.PercentOfSalesFinanced);
        } else {
          return (
            (percentValue >= 0 && percentValue <= 100) || getMessageForInvalidFields(InputLabel.PercentOfSalesFinanced)
          );
        }
      },
    });
    register('intermediary_years_in_business', {
      required: {
        value: !watcher.intermediary_years_in_business,
        message: getMessageForRequiredFields(InputLabel.YearsInBusiness),
      },
      validate: (value) => isValidYearsOfBusiness(value),
    });
    register('intermediary_states_of_operation', {
      required: {
        value: !watcher.intermediary_states_of_operation.length,
        message: DEFAULT_REQUIRED_MESSAGE,
      },
    });
    register('intermediary_other_home_improvement_category', {
      required: false,
    });
    register('same_address', {
      value: false,
    });
  }, [register, unregister, watcher]);

  useEffect(() => {
    if (
      watcher.intermediary_primary_home_improvement_category === PrimaryHomeImprovementCategory.Other ||
      watcher.intermediary_other_home_improvement_category.includes(PrimaryHomeImprovementCategory.Other)
    ) {
      register('intermediary_other_home_improvement_type', {
        required: {
          value: !watcher.intermediary_other_home_improvement_type,
          message: DEFAULT_REQUIRED_MESSAGE,
        },
      });
    } else {
      unregister('intermediary_other_home_improvement_type');
    }
  }, [
    register,
    unregister,
    watcher.intermediary_other_home_improvement_category,
    watcher.intermediary_other_home_improvement_type,
    watcher.intermediary_primary_home_improvement_category,
  ]);

  useEffect(() => {
    if (watcher.intermediary_do_you_install_the_products_you_sell === InstallProductsYouSell.No) {
      register('intermediary_installer_count', {
        required: {
          value: !watcher.intermediary_installer_count,
          message: DEFAULT_REQUIRED_MESSAGE,
        },
        validate: (value) => {
          return !!removeNonDigits(value) || DEFAULT_REQUIRED_MESSAGE;
        },
      });
    }

    return () => {
      unregister('intermediary_installer_count');
    };
  }, [register, unregister, watcher.intermediary_do_you_install_the_products_you_sell]);

  const handleSameAddress = () => {
    setValue('same_address', !watcher.same_address);

    if (!watcher.same_address) {
      setValue('intermediary_mailing_address', watcher.intermediary_corporate_address);
      setValue('intermediary_city', watcher.intermediary_corporate_city);
      setValue('intermediary_state', watcher.intermediary_corporate_state);
      setValue('intermediary_zip_code', watcher.intermediary_corporate_zip_code);
    }

    trigger('same_address');
  };

  const handleCopyPaste = (event: React.ClipboardEvent) => event.preventDefault();

  const onErrors = () => {
    notificationManager.notifyFailure(FailureNotificationType.InputsValidationErrors);
  };

  const onSubmit = async (data: FormDataContractorApplication) => {
    const application: IntermediaryApplicationProps = {
      intermediary_corporate_entity_name: data.intermediary_corporate_entity_name,
      intermediary_business_name: data.intermediary_business_name,
      intermediary_federal_tax_id: data.intermediary_federal_tax_id.replace(/\D/g, ''),
      intermediary_business_entity_structure: data.intermediary_business_entity_structure as BusinessEntityStructure,
      intermediary_year_of_incorporation: data.intermediary_year_of_incorporation,
      intermediary_phone: data.intermediary_phone,
      intermediary_email: data.intermediary_email?.toLowerCase(),
      intermediary_corporate_address_information: {
        streetNumber: getStreetNumberFromStreetAddress(data.intermediary_corporate_address),
        streetName: getStreetNameFromStreetAddress(data.intermediary_corporate_address),
        city: data.intermediary_corporate_city,
        stateOrProvince: data.intermediary_corporate_state,
        zipOrPostalCode: data.intermediary_corporate_zip_code,
      },
      intermediary_mailing_address_information: {
        streetNumber: getStreetNumberFromStreetAddress(data.intermediary_mailing_address),
        streetName: getStreetNameFromStreetAddress(data.intermediary_mailing_address),
        city: data.intermediary_city,
        stateOrProvince: data.intermediary_state,
        zipOrPostalCode: data.intermediary_zip_code,
      },
      intermediary_website_or_facebook_page: data.intermediary_website_or_facebook_page,
      intermediary_states_of_operation: data.intermediary_states_of_operation,
      intermediary_do_you_install_the_products_you_sell:
        data.intermediary_do_you_install_the_products_you_sell === InstallProductsYouSell.Yes,
      intermediary_installer_count: +data.intermediary_installer_count,
      intermediary_associated_manufacturer_1: data.intermediary_associated_manufacturer_1,
      intermediary_associated_manufacturer_2: data.intermediary_associated_manufacturer_2,
      intermediary_annual_sales: +removeNonDigits(data.intermediary_annual_sales),
      intermediary_average_jobs: +data.intermediary_average_jobs,
      intermediary_years_in_business: +data.intermediary_years_in_business,
      intermediary_hold_appropriate_license: data.intermediary_hold_appropriate_license === HoldLicense.Yes,
      intermediary_hold_appropriate_license_description: data.intermediary_hold_appropriate_license_description || null,
      intermediary_main_contact_first_name: data.intermediary_main_contact_first_name,
      intermediary_main_contact_last_name: data.intermediary_main_contact_last_name,
      intermediary_main_contact_phone: data.intermediary_main_contact_phone,
      intermediary_main_contact_email: data.intermediary_main_contact_email?.toLowerCase(),
      intermediary_primary_home_improvement_category:
        data.intermediary_primary_home_improvement_category as PrimaryHomeImprovementCategory,
      intermediary_other_home_improvement_category:
        data.intermediary_other_home_improvement_category as PrimaryHomeImprovementCategory[],
      intermediary_main_contact_position: data.intermediary_main_contact_position as MainContactPosition,
      intermediary_percent_of_sales_financed: +data.intermediary_percent_of_sales_financed / 100,
      intermediary_total_owners: convertTotalOwnersValueToNumber(data.intermediary_total_owners),
      intermediary_owners: prepareTotalOwnersArray(data),
      intermediary_how_did_you_hear_about_us: data.intermediary_how_did_you_hear_about_us,
      intermediary_other_home_improvement_type: data.intermediary_other_home_improvement_type || '',
      intermediary_who_signed_master_dealer_agreement: data.intermediary_who_signed_master_dealer_agreement,
      intermediary_authorized_signer_first_name: data.intermediary_authorized_signer_first_name || '',
      intermediary_authorized_signer_last_name: data.intermediary_authorized_signer_last_name || '',
      intermediary_authorized_signer_position: data.intermediary_authorized_signer_position || '',
      intermediary_prequal_link_used: url.href,
    };

    identityFullStory(
      data.intermediary_email?.toLowerCase(),
      `${data.intermediary_main_contact_first_name}_${data.intermediary_main_contact_last_name}`,
    );

    dispatch(
      setContractorPortalData({
        contractorApplication: application,
        alreadyExistingEmail: data.intermediary_email?.toLowerCase(),
      }),
    );

    await dispatch(saveContractorApplication(application)).unwrap();
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <FormHeader
            title="Contractor Application"
            subtitle={`Start offering your customers direct access to home improvement financing through HFS.\nYou are one step away from joining the HFS "You Dream It, We Finance It" network of contractors.`}
            classNameTitle={styles.form_title}
          />
          <FormProvider {...formMethods}>
            <FormBody onSubmit={handleSubmit(onSubmit, onErrors)}>
              <div className={styles.form_row}>
                <Input
                  label={InputLabel.CorporateEntityName}
                  name="intermediary_corporate_entity_name"
                  value={watcher.intermediary_corporate_entity_name}
                  errorMessage={errors.intermediary_corporate_entity_name?.message}
                  inputContainerClassName={styles.input}
                  onChange={(event) => {
                    clearErrors('intermediary_corporate_entity_name');
                    setValue('intermediary_corporate_entity_name', event.target.value);
                  }}
                  data-hj-allow
                />
                <Input
                  label={
                    <>
                      {InputLabel.BusinessTradeName}
                      <>
                        <Tooltip title={BUSINESS_TRADE_NAME_TOOLTIP_TEXT}>
                          <InfoIcon className={styles.iconInfo} />
                        </Tooltip>
                      </>
                    </>
                  }
                  name="intermediary_business_name"
                  value={watcher.intermediary_business_name}
                  errorMessage={errors.intermediary_business_name?.message}
                  inputContainerClassName={styles.input}
                  onChange={(event) => {
                    clearErrors('intermediary_business_name');
                    setValue('intermediary_business_name', event.target.value);
                  }}
                  data-hj-allow
                />
              </div>
              <div className={styles.form_row}>
                <Input
                  label={InputLabel.FedTaxId}
                  name="intermediary_federal_tax_id"
                  value={einMask(watcher.intermediary_federal_tax_id)}
                  errorMessage={errors.intermediary_federal_tax_id?.message}
                  maxLength={FEDERAL_TAX_ID_WITH_DASH_MAX_LENGTH}
                  inputContainerClassName={styles.input}
                  onChange={(event) => {
                    clearErrors('intermediary_federal_tax_id');
                    setValue('intermediary_federal_tax_id', event.target.value);
                  }}
                  inputMode="numeric"
                />
              </div>
              <div className={styles.form_row}>
                <Select
                  label={InputLabel.BusinessEntity}
                  name="intermediary_business_entity_structure"
                  useFormControl
                  control={control}
                  required
                  options={BUSINESS_ENTITY_STRUCTURE_OPTIONS}
                  value={BUSINESS_ENTITY_STRUCTURE_OPTIONS.find(
                    (option) => option.label === watcher.intermediary_business_entity_structure,
                  )}
                  errorMessage={errors?.intermediary_business_entity_structure?.message}
                  inputContainerClassName={styles.input}
                  className={styles.dropdown}
                  isClearable={!!watcher.intermediary_business_entity_structure}
                />
                <NumberInput
                  label={InputLabel.YearOfBusinessIncorporation}
                  name={'intermediary_year_of_incorporation'}
                  value={watcher.intermediary_year_of_incorporation}
                  errorMessage={errors?.intermediary_year_of_incorporation?.message}
                  className={styles.input}
                  onChange={(event) => {
                    clearErrors('intermediary_year_of_incorporation');
                    setValue('intermediary_year_of_incorporation', event.target.value);
                  }}
                  onBlur={() => {
                    if (watcher.intermediary_year_of_incorporation) {
                      trigger('intermediary_year_of_incorporation');
                    }
                  }}
                  format="####"
                  placeholder="YYYY"
                  mask="_"
                  allowEmptyFormatting
                />
              </div>
              <div className={styles.form_row}>
                <PhoneNumberInput
                  label={InputLabel.MainBusinessPhoneNumber}
                  name="intermediary_phone"
                  className={styles.input}
                  inputContainerClassname={styles.input}
                  value={watcher.intermediary_phone}
                  errorMessage={errors.intermediary_phone?.message}
                  onChange={(value: string) => {
                    clearErrors('intermediary_phone');
                    setValue('intermediary_phone', value);
                  }}
                  onBlur={() => {
                    if (watcher.intermediary_phone) {
                      trigger('intermediary_phone');
                    }
                  }}
                />
                <Input
                  label={InputLabel.BusinessEmailAddress}
                  name="intermediary_email"
                  value={watcher.intermediary_email?.toLowerCase()}
                  errorMessage={errors?.intermediary_email?.message}
                  inputContainerClassName={styles.input}
                  onChange={(event) => {
                    clearErrors('intermediary_email');
                    setValue('intermediary_email', event.target.value);
                  }}
                  onBlur={() => {
                    if (watcher.intermediary_email) {
                      trigger('intermediary_email');
                    }
                  }}
                  onCopy={handleCopyPaste}
                />
              </div>
              <SearchLocationInput
                containerClassName={styles.select_input}
                initialValue={{
                  streetAddress: watcher.intermediary_corporate_address,
                  state: watcher.intermediary_corporate_state,
                  city: watcher.intermediary_corporate_city,
                  zip: watcher.intermediary_corporate_zip_code,
                }}
                streetLabel={InputLabel.CorporateAddress}
                zipLabel={InputLabel.CorporateZip}
                cityLabel={InputLabel.CorporateCity}
                stateLabel={InputLabel.CorporateState}
                control={control}
                useFormControl
                inputNames={{
                  streetAddress: 'intermediary_corporate_address',
                  city: 'intermediary_corporate_city',
                  state: 'intermediary_corporate_state',
                  zip: 'intermediary_corporate_zip_code',
                }}
                required
                errors={{
                  streetAddress: errors.intermediary_corporate_address,
                  state: errors.intermediary_corporate_state,
                  city: errors.intermediary_corporate_city,
                  zip: errors.intermediary_corporate_zip_code,
                }}
                inputChange={(name, value) => {
                  setValue(name as keyof FormDataContractorApplication, value);

                  const sameAddressName = getSameAddressVariable(name);

                  if (watcher.same_address && sameAddressName) {
                    setValue(sameAddressName as keyof FormDataContractorApplication, value);
                  }

                  if (formState.isSubmitted) {
                    trigger(name as keyof FormDataContractorApplication);
                  }
                }}
                getValues={getValues}
              />
              <div className={styles.form_row}>
                <Checkbox
                  type={CheckboxType.Alternative}
                  name="same_address"
                  text={InputLabel.SameAddress}
                  isChecked={watcher.same_address}
                  onChange={() => handleSameAddress()}
                />
              </div>
              <SearchLocationInput
                containerClassName={styles.select_input}
                initialValue={{
                  streetAddress: watcher.intermediary_mailing_address,
                  state: watcher.intermediary_state,
                  city: watcher.intermediary_city,
                  zip: watcher.intermediary_zip_code,
                }}
                control={control}
                useFormControl
                required
                streetLabel={InputLabel.MailingAddress}
                zipLabel={InputLabel.MailingZip}
                cityLabel={InputLabel.MailingCity}
                stateLabel={InputLabel.MailingState}
                inputNames={{
                  streetAddress: 'intermediary_mailing_address',
                  city: 'intermediary_city',
                  state: 'intermediary_state',
                  zip: 'intermediary_zip_code',
                }}
                errors={{
                  streetAddress: errors.intermediary_mailing_address,
                  state: errors.intermediary_state,
                  city: errors.intermediary_city,
                  zip: errors.intermediary_zip_code,
                }}
                inputChange={(name, value) => {
                  setValue(name as keyof FormDataContractorApplication, value);

                  const sameAddressName = getSameAddressVariable(name);

                  if (watcher.same_address && sameAddressName) {
                    setValue(sameAddressName as keyof FormDataContractorApplication, value);
                  }

                  if (formState.isSubmitted) {
                    trigger(name as keyof FormDataContractorApplication);
                  }
                }}
                getValues={getValues}
              />
              <div className={styles.form_row}>
                <Input
                  label={InputLabel.CompanyWebsite}
                  name="intermediary_website_or_facebook_page"
                  value={watcher.intermediary_website_or_facebook_page}
                  errorMessage={errors.intermediary_website_or_facebook_page?.message}
                  inputContainerClassName={styles.input}
                  onChange={(event) => {
                    clearErrors('intermediary_website_or_facebook_page');
                    setValue('intermediary_website_or_facebook_page', event.target.value);
                  }}
                  onBlur={() => {
                    if (watcher.intermediary_website_or_facebook_page) {
                      trigger('intermediary_website_or_facebook_page');
                    }
                  }}
                />
              </div>
              <div className={styles.form_row}>
                <MultipleSelect
                  id="states"
                  selectOptions={states}
                  selectAllLabel="All States"
                  useSelectAll
                  errorMessage={(errors.intermediary_states_of_operation as unknown as FieldError)?.message}
                  inputLabel={InputLabel.SelectStatesWhereYouOperate}
                  initialValues={
                    watcher.intermediary_states_of_operation.map((stateOperation: string) =>
                      states.find((option) => option.value === stateOperation),
                    ) as MultipleSelectOption[]
                  }
                  onChange={(options) => {
                    clearErrors('intermediary_states_of_operation');
                    setValue(
                      'intermediary_states_of_operation',
                      options.map((option) => option.value),
                    );
                  }}
                />
              </div>
              <div className={styles.form_row}>
                <Select
                  name="intermediary_primary_home_improvement_category"
                  useFormControl
                  control={control}
                  required
                  label={'Primary Home Improvement Category'}
                  options={PRIMARY_HOME_IMPROVEMENT_CATEGORY_OPTIONS}
                  value={PRIMARY_HOME_IMPROVEMENT_CATEGORY_OPTIONS.find(
                    (option) => option.label === watcher.intermediary_primary_home_improvement_category,
                  )}
                  onChange={() => {
                    setValue('intermediary_other_home_improvement_category', []);
                  }}
                  errorMessage={errors?.intermediary_primary_home_improvement_category?.message}
                  inputContainerClassName={styles.input}
                  className={styles.dropdown}
                  isClearable={!!watcher.intermediary_primary_home_improvement_category}
                />
              </div>
              <div className={styles.form_row}>
                <MultipleSelect
                  id="home-categories"
                  selectOptions={PRIMARY_HOME_IMPROVEMENT_CATEGORY_OPTIONS.filter(
                    (option) => option.label !== watcher.intermediary_primary_home_improvement_category,
                  )}
                  useSelectAll
                  selectAllLabel="All categories"
                  errorMessage={(errors.intermediary_other_home_improvement_category as unknown as FieldError)?.message}
                  inputLabel={InputLabel.OtherHomeImprovementCategories}
                  initialValues={
                    watcher.intermediary_other_home_improvement_category.map((category: string) =>
                      PRIMARY_HOME_IMPROVEMENT_CATEGORY_OPTIONS.find((option) => option.value === category),
                    ) as MultipleSelectOption[]
                  }
                  onChange={(options) => {
                    clearErrors('intermediary_other_home_improvement_category');
                    setValue(
                      'intermediary_other_home_improvement_category',
                      options.map((option) => option.value),
                    );
                  }}
                />
              </div>
              {(watcher.intermediary_primary_home_improvement_category === PrimaryHomeImprovementCategory.Other ||
                watcher.intermediary_other_home_improvement_category.includes(
                  PrimaryHomeImprovementCategory.Other,
                )) && (
                <div className={styles.form_row}>
                  <Input
                    label={InputLabel.PleaseSpecifyHomeImprovementType}
                    name="intermediary_other_home_improvement_type"
                    value={watcher.intermediary_other_home_improvement_type || ''}
                    errorMessage={errors.intermediary_other_home_improvement_type?.message}
                    inputContainerClassName={styles.input}
                    onChange={(event) => {
                      clearErrors('intermediary_other_home_improvement_type');
                      setValue('intermediary_other_home_improvement_type', event.target.value);
                    }}
                  />
                </div>
              )}
              <div className={styles.form_row}>
                <Select
                  label={InputLabel.DoYouInstallTheProductsYouCell}
                  name="intermediary_do_you_install_the_products_you_sell"
                  useFormControl
                  control={control}
                  required
                  options={DO_YOU_INSTALL_PRODUCTS_OPTIONS}
                  value={DO_YOU_INSTALL_PRODUCTS_OPTIONS.find(
                    (option) => option.label === watcher.intermediary_do_you_install_the_products_you_sell,
                  )}
                  errorMessage={errors?.intermediary_do_you_install_the_products_you_sell?.message}
                  inputContainerClassName={styles.input}
                  onChange={(event: any) => {
                    clearErrors('intermediary_do_you_install_the_products_you_sell');
                    setValue(
                      'intermediary_do_you_install_the_products_you_sell',
                      event.value as InstallProductsYouSell,
                    );
                  }}
                  className={styles.dropdown}
                  isClearable={!!watcher.intermediary_do_you_install_the_products_you_sell}
                />
              </div>
              {watcher.intermediary_do_you_install_the_products_you_sell === InstallProductsYouSell.No && (
                <div className={styles.form_row}>
                  <Input
                    label={InputLabel.HowManyInstallersDoYouHave}
                    name="intermediary_installer_count"
                    value={removeNonDigits(watcher.intermediary_installer_count)}
                    errorMessage={errors.intermediary_installer_count?.message}
                    inputContainerClassName={styles.input}
                    inputMode="numeric"
                    onChange={(event) => {
                      clearErrors('intermediary_installer_count');
                      setValue('intermediary_installer_count', event.target.value);
                    }}
                  />
                </div>
              )}
              <div className={styles.form_row}>
                <Input
                  label={
                    <>
                      {InputLabel.AssociatedManufacturer1}
                      <>
                        <Tooltip title={ASSOCIATED_MANUFACTURE_TOOLTIP_TEXT}>
                          <InfoIcon className={styles.iconInfo} />
                        </Tooltip>
                      </>
                    </>
                  }
                  name="intermediary_associated_manufacturer_1"
                  value={watcher.intermediary_associated_manufacturer_1}
                  errorMessage={errors.intermediary_associated_manufacturer_1?.message}
                  inputContainerClassName={styles.input}
                  onChange={(event: any) => {
                    clearErrors('intermediary_associated_manufacturer_1');
                    setValue('intermediary_associated_manufacturer_1', event.target.value);
                  }}
                />

                <Input
                  label={
                    <>
                      {InputLabel.AssociatedManufacturer2}
                      <>
                        <Tooltip title={ASSOCIATED_MANUFACTURE_TOOLTIP_TEXT}>
                          <InfoIcon className={styles.iconInfo} />
                        </Tooltip>
                      </>
                    </>
                  }
                  name="intermediary_associated_manufacturer_2"
                  value={watcher.intermediary_associated_manufacturer_2}
                  errorMessage={errors.intermediary_associated_manufacturer_2?.message}
                  inputContainerClassName={styles.input}
                  onChange={(event: any) => {
                    clearErrors('intermediary_associated_manufacturer_2');
                    setValue('intermediary_associated_manufacturer_2', event.target.value);
                  }}
                />
              </div>
              <div className={styles.form_row}>
                <NumberInput
                  label={InputLabel.AnnualRevenue}
                  name={'intermediary_annual_sales'}
                  value={watcher.intermediary_annual_sales?.toString()}
                  errorMessage={errors.intermediary_annual_sales?.message}
                  className={styles.input}
                  prefix={MONETARY_PREFIX}
                  placeholder={MONETARY_PREFIX}
                  thousandSeparator
                  onChange={(event) => {
                    clearErrors('intermediary_annual_sales');
                    setValue('intermediary_annual_sales', event.target.value);
                  }}
                  allowNegative={false}
                />
                <NumberInput
                  label={InputLabel.JobsSoldPerYear}
                  name={'intermediary_average_jobs'}
                  value={watcher.intermediary_average_jobs}
                  errorMessage={errors.intermediary_average_jobs?.message}
                  className={styles.input}
                  onChange={(event) => {
                    clearErrors('intermediary_average_jobs');
                    setValue('intermediary_average_jobs', event.target.value);
                  }}
                  allowNegative={false}
                  pattern="\d*"
                />
              </div>
              <div className={styles.form_row}>
                <Input
                  label={InputLabel.PercentOfSalesFinanced}
                  name="intermediary_percent_of_sales_financed"
                  inputContainerClassName={styles.input}
                  placeholder={PERCENT_PLACEHOLDER}
                  errorMessage={errors.intermediary_percent_of_sales_financed?.message}
                  onChange={(event: any) => {
                    clearErrors('intermediary_percent_of_sales_financed');
                    setValue('intermediary_percent_of_sales_financed', event.target.value);
                  }}
                  value={convertPercentageValue(watcher.intermediary_percent_of_sales_financed)}
                />
                <NumberInput
                  label={InputLabel.YearsInBusiness}
                  name="intermediary_years_in_business"
                  value={watcher.intermediary_years_in_business}
                  errorMessage={errors.intermediary_years_in_business?.message}
                  maxLength={YEARS_IN_BUSINESS_MAX_LENGTH}
                  inputMode="numeric"
                  className={styles.input}
                  onChange={(event) => {
                    clearErrors('intermediary_years_in_business');
                    setValue('intermediary_years_in_business', event.target.value);
                  }}
                />
              </div>
              <HoldAppropriateLicense />
              <div className={styles.form_row}>
                <Input
                  label={InputLabel.HowDidYouHearAboutHfs}
                  name="intermediary_how_did_you_hear_about_us"
                  value={watcher.intermediary_how_did_you_hear_about_us}
                  errorMessage={errors.intermediary_how_did_you_hear_about_us?.message}
                  inputContainerClassName={styles.input}
                  onChange={(event) => {
                    clearErrors('intermediary_how_did_you_hear_about_us');
                    setValue('intermediary_how_did_you_hear_about_us', event.target.value);
                  }}
                  onBlur={() => {
                    if (watcher.intermediary_how_did_you_hear_about_us) {
                      trigger('intermediary_how_did_you_hear_about_us');
                    }
                  }}
                />
              </div>
              <ContactPersonInformation />
              <OwnerInformation />
              <MasterDialerAgreement />
            </FormBody>
          </FormProvider>
          <div className={clsx('buttons', styles.submit_button)}>
            <Button type="submit" form="form">
              Submit
            </Button>
          </div>
        </>
      )}
    </>
  );
};

export default ContractorApplication;
